import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  MonitoringExportMapFeature,
  MonitoringExportMapType,
  MonitoringExportUIFeature,
} from 'app/views/monitoring/components/monitoring-export-gate/useMonitoringExportState.types';
import Guid from 'core/types/Guid';
import { resetStore } from '../actions';
import { MonitorsMapControlOption } from './constants';
import { MonitoringExportConfig, MonitoringExportParamName } from './types';

export type MonitoringState = {
  map: {
    controls: {
      monitors: MonitorsMapControlOption;
    };
  };
  isAddingMonitor: boolean;
  preferredMonitoringView: 'MAP' | 'GRID';
  export: MonitoringExportConfig;
};

export const INITIAL_EXPORT_STATE: MonitoringExportConfig = {
  showUi: [],
  showFeature: [],
  mapType: 'satellite-greyscale',
  measurementGroup: { enabled: false, value: null },
  mapTime: { enabled: false, value: null },
};

export const INITIAL_STATE: MonitoringState = {
  map: {
    controls: {
      monitors: 'all',
    },
  },
  isAddingMonitor: false,
  preferredMonitoringView: 'MAP',
  export: INITIAL_EXPORT_STATE,
};

export const slice = createSlice({
  name: 'monitoring',
  initialState: INITIAL_STATE,
  reducers: {
    setMonitorsMapControl: (state, { payload }: PayloadAction<MonitorsMapControlOption>) => {
      state.map.controls.monitors = payload;
    },

    setIsAddingMonitor: (state, { payload }: PayloadAction<boolean>) => {
      state.isAddingMonitor = payload;
    },

    setPreferredMonitoringView: (state, { payload }: PayloadAction<'MAP' | 'GRID'>) => {
      state.preferredMonitoringView = payload;
    },

    setExportUiFeature: (
      state,
      { payload: [feature, isShown] }: PayloadAction<[MonitoringExportUIFeature, boolean]>
    ) => {
      const newShowUiState = state.export.showUi.filter(v => v !== feature);
      if (isShown) {
        state.export.showUi = [...newShowUiState, feature];
      } else {
        state.export.showUi = newShowUiState;
      }
    },

    setExportMapFeature: (
      state,
      { payload: [feature, isShown] }: PayloadAction<[MonitoringExportMapFeature, boolean]>
    ) => {
      const newShowFeatureState = state.export.showFeature.filter(v => v !== feature);
      if (isShown) {
        state.export.showFeature = [...newShowFeatureState, feature];
      } else {
        state.export.showFeature = newShowFeatureState;
      }
    },

    setExportParamEnabled: (
      state,
      { payload: [param, isEnabled] }: PayloadAction<[MonitoringExportParamName, boolean]>
    ) => {
      state.export[param].enabled = isEnabled;
    },

    setExportMapType: (state, { payload }: PayloadAction<MonitoringExportMapType>) => {
      state.export.mapType = payload;
    },

    setExportMeasurementGroup: (state, { payload }: PayloadAction<Guid | null>) => {
      state.export.measurementGroup.value = payload;
    },

    setExportMapTime: (state, { payload }: PayloadAction<number | null>) => {
      state.export.mapTime.value = payload;
    },

    resetExportConfig: state => {
      state.export = INITIAL_EXPORT_STATE;
    },
  },
  extraReducers: builder => {
    builder.addCase(resetStore.type, () => {
      return INITIAL_STATE;
    });
  },
});
